import {
  deleteImageTemplateMappingDataAsync,
  saveImageTemplateMappingForPlatformAsync,
} from 'src/redux/actions/templateAndPricingActions';
import {
  buildImageMappingSavePayload,
  extractImageTemplateMappingId,
  hasImageMappingUserInput,
  type ImageMappingIsDefaultPayloadKey,
  type ImageMappingMeta,
  type ImageMappingRow,
} from './listingProfileImageMappingUtils';

const readDeleteError = (result: any): string | undefined =>
  result?.payload?.data?.error ??
  result?.payload?.data?.detail ??
  result?.payload?.error ??
  result?.error?.message;

export async function deleteListingProfileImageMappingRow(options: {
  dispatch: any;
  platformId: number;
  imageTemplateMappingId: number | null;
  row: ImageMappingRow;
}): Promise<{ ok: boolean; error?: string }> {
  const rowId = options.row.id;
  const templateId = options.imageTemplateMappingId;
  const needsRemoteDelete =
    rowId != null &&
    rowId > 0 &&
    templateId != null &&
    templateId > 0 &&
    options.platformId > 0;

  if (!needsRemoteDelete) {
    return { ok: true };
  }

  try {
    const result = await options.dispatch(
      deleteImageTemplateMappingDataAsync({
        platformId: options.platformId,
        imageTemplateMappingId: templateId,
        imageTemplateMappingDataId: rowId,
      })
    );
    if (deleteImageTemplateMappingDataAsync.rejected.match(result)) {
      const error = readDeleteError(result);
      return { ok: false, error: String(error ?? 'Image mapping row delete failed') };
    }
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: err?.message ?? 'Image mapping row delete failed' };
  }
}

export async function persistListingProfileImageMapping(options: {
  dispatch: any;
  destinationPlatformId: number;
  templateMappingId: number;
  ruleName: string;
  imageTemplateMappingId: number | null;
  meta: ImageMappingMeta;
  rows: ImageMappingRow[];
  includeIsDefault?: boolean;
  isDefault?: boolean;
  isDefaultPayloadKey?: ImageMappingIsDefaultPayloadKey;
}): Promise<{ ok: boolean; error?: string; imageTemplateMappingId?: number | null }> {
  if (!hasImageMappingUserInput(options.rows, options.meta)) {
    return { ok: true, imageTemplateMappingId: options.imageTemplateMappingId };
  }

  const payload = buildImageMappingSavePayload({
    imageTemplateMappingId: options.imageTemplateMappingId,
    destinationPlatformId: options.destinationPlatformId,
    templateMappingId: options.templateMappingId,
    ruleName: options.ruleName,
    meta: options.meta,
    rows: options.rows,
    includeIsDefault: options.includeIsDefault,
    isDefault: options.isDefault,
    isDefaultPayloadKey: options.isDefaultPayloadKey,
  });

  try {
    const result: any = await options.dispatch(saveImageTemplateMappingForPlatformAsync(payload));
    const error =
      result?.payload?.data?.error ??
      result?.payload?.error ??
      result?.error?.message;
    if (error) {
      return { ok: false, error: String(error) };
    }
    return {
      ok: true,
      imageTemplateMappingId:
        extractImageTemplateMappingId(result?.payload) ?? options.imageTemplateMappingId,
    };
  } catch (err: any) {
    return { ok: false, error: err?.message ?? 'Image mapping save failed' };
  }
}
